import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Fade, Modal, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import deletePopupImg from "../../../assets/images/png/Group 2@2x.png";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import QuotationItemsTable from "./QuotationItemsTable";

import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";

import styles from "../../VendorsTabs/InvoiceProcess/InvoiceProcess.module.css";
import { API_ENDPOINT } from "../../../services/api/resources";

const safeLower = (value) =>
  (value === undefined || value === null ? "" : String(value)).toLowerCase();

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const normalized = String(value)
    .replace(/[, ]/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatIN = (value) =>
  Number.isFinite(value)
    ? value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "-";

const formatRupees = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") return `₹${formatIN(value)}`;

  const trimmed = String(value).trim();
  if (!trimmed) return "-";
  if (trimmed === "-") return "-";
  if (trimmed.startsWith("₹")) return trimmed;
  return `₹${trimmed}`;
};

const safeText = (value, fallback = "-") => {
  const v = value === undefined || value === null ? "" : String(value).trim();
  return v ? v : fallback;
};

const uniqTexts = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((v) => safeText(v, ""))
        .filter((v) => v),
    ),
  );

const computeTotals = (items = []) => {
  const list = Array.isArray(items) ? items : [];
  let gst = 0;
  let total = 0;

  list.forEach((it) => {
    const unit = toNumber(it?.unit_price);
    const taxPct = toNumber(it?.tax);
    const lineGst = taxPct ? (unit * taxPct) / 100 : 0;
    const lineTotal = unit + lineGst;
    gst += lineGst;
    total += lineTotal;
  });

  return { gst, total };
};

const buildDocumentUrl = (documentPath = "") => {
  const doc = String(documentPath || "").trim();
  if (!doc || doc.toLowerCase() === "none") return "";
  const base = String(API_ENDPOINT?.Base_URL || "").replace(/\/+$/, "");
  const cdnBase = String(process.env.REACT_APP_CLOUDFRONT_URL || "").replace(/\/+$/, "");
  const lower = doc.toLowerCase();
  const isAbsolute = lower.startsWith("http://") || lower.startsWith("https://");
  if (isAbsolute) return doc;

  const cleanDoc = doc.replace(/^\/+/, "");
  // Prefer CDN for relative asset paths when available
  if (cdnBase && (cleanDoc.startsWith("assets/") || cleanDoc.startsWith("media/"))) {
    return `${cdnBase}/${cleanDoc}`;
  }

  // Fallback to backend base
  return `${base}/${cleanDoc}`;
};

const UnregisteredVendorCatalogTab = () => {
  const navigate = useNavigate();
  const loginData = useSelector((state) => state.loginData.value);
  const authToken =
    loginData?.auth_token || loginData?.access_token || loginData?.token || "";
  const vendorToken =
    loginData?.vendor_token ||
    loginData?.user?.token ||
    loginData?.user?.user_token ||
    "";

  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  const [tableProps, setTableProps] = useState({
    isLoading: false,
    errorMessage: "",
  });

  const [paginationData, setPaginationData] = useState({
    total_count: 0,
    current_page_count: 0,
    page_count: 1,
    page: 1,
  });

  const [quotations, setQuotations] = useState([]);

  const [deleteQuotationOpen, setDeleteQuotationOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    title: "",
    description: "",
    icon: successPopupImg,
  });

  const fetchQuotations = useCallback(
    async ({ page = pageNumber, q = search } = {}) => {
      if (!vendorToken) return;

      setTableProps({ isLoading: true, errorMessage: "" });
      try {
        const res = await axios.post(
          API_ENDPOINT.get_unregistered_vendor_quotation_doc,
          {
            vendor_token: vendorToken,
            page,
            search: q || "",
          },
          authToken ? { headers: { Authorization: `Token ${authToken}` } } : undefined,
        );

        const data = res?.data || {};
        setPaginationData({
          total_count: Number(data.total_count ?? 0),
          current_page_count: Number(data.current_page_count ?? 0),
          page_count: Number(data.page_count ?? 1),
          page: Number(data.page ?? page),
        });
        setQuotations(Array.isArray(data.results) ? data.results : []);
        setTableProps({ isLoading: false, errorMessage: "" });
      } catch (err) {
        setQuotations([]);
        setTableProps({
          isLoading: false,
          errorMessage:
            err?.response?.data?.message ||
            err?.response?.data?.response ||
            "Unable to fetch quotations.",
        });
      }
    },
    [vendorToken, authToken, pageNumber, search],
  );

  useEffect(() => {
    fetchQuotations({ page: pageNumber, q: search });
  }, [fetchQuotations, pageNumber]);

  const [viewOpen, setViewOpen] = useState(false);
  const [activeQuotation, setActiveQuotation] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [commonTextOpen, setCommonTextOpen] = useState(false);
  const [commonTextTitle, setCommonTextTitle] = useState("");
  const [commonTextValue, setCommonTextValue] = useState("");

  const itemLinkStyle = useMemo(
    () => ({
      background: "transparent",
      border: "none",
      padding: 0,
      cursor: "pointer",
      color: "#2563eb",
      textDecoration: "underline",
      fontFamily: "inherit",
      fontSize: "inherit",
      display: "inline-block",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    [],
  );

  const headings = useMemo(
    () => [
      {
        label: "Quotation Name",
        field: "quotation_name",
        col_width: "280px",
        sortable: false,
      },
      {
        label: "Item Name",
        field: "item_name",
        col_width: "220px",
        sortable: false,
        noWrap: true,
      },
      {
        label: "Basic Price",
        field: "unit_price",
        col_width: "180px",
        sortable: false,
        noWrap: true,
      },
      // { label: "Tax", field: "tax", col_width: "130px", sortable: false, noWrap: true },
      // {
      //   label: "Grand Price",
      //   field: "grand_total",
      //   col_width: "170px",
      //   sortable: false,
      //   noWrap: true,
      // },
      {
        label: "Created Date",
        field: "created_date",
        col_width: "230px",
        sortable: false,
        noWrap: true,
      },
    ],
    [],
  );

  const tableData = useMemo(() => {
    const list = Array.isArray(quotations) ? quotations : [];
    return list.map((q, qIdx) => {
      const items = Array.isArray(q?.items) ? q.items : [];
      const totals = computeTotals(items);

      const quotationToken = safeText(q?.quotation_token || q?.quotationToken || q?.id, "");
      console.log(q,"quotation");
      
      const quotationName = safeText(
        q?.company_name||
       
          q?.file_name ||
         
          `Quotation ${qIdx + 1}`,
      );

      const createdDate = safeText(q?.created_date);
      const firstItemName = safeText(items?.[0]?.item_name);
      const firstUnitPrice = formatRupees(items?.[0]?.unit_price ?? "-");

      const uniqueTax = uniqTexts(items.map((it) => it?.tax));
      const taxDisplay =
        items.length <= 1
          ? safeText(items?.[0]?.tax, "-")
          : uniqueTax.length === 1
            ? uniqueTax[0]
            : "Multiple items";

      const baseQuotation = {
        token: quotationToken || `q-${qIdx}`,
        quotation_token: quotationToken,
        vendor_token: safeText(q?.vendor_token, ""),
        quotation_name: quotationName,
        document: safeText(q?.document, ""),
        file_name: safeText(q?.file_name, ""),
        created_date: createdDate,
        items,
        totals,
      };

      const openView = () => {
        setActiveQuotation(baseQuotation);
        setItemSearch("");
        setViewOpen(true);
      };

      return {
        token: baseQuotation.token,
        quotation_token: baseQuotation.quotation_token,
        quotation_name: quotationName,
        item_name: items.length <= 1 ? firstItemName : "Multiple items",
        unit_price: items.length <= 1 ? firstUnitPrice : "Multiple items",
        tax: taxDisplay,
        grand_total: formatRupees(totals.total),
        created_date: createdDate,
        __quotation: baseQuotation,
        __openView: openView,
        __searchText: [
          quotationName,
          createdDate,
          items.map((it) => it?.item_name).filter(Boolean).join(" "),
          items.map((it) => it?.unit_price).filter(Boolean).join(" "),
          items.map((it) => it?.tax).filter((v) => v !== null && v !== undefined).join(" "),
          formatRupees(totals.total),
        ].join(" "),
      };
    });
  }, [quotations]);

  const filteredData = useMemo(() => {
    const q = safeLower(search).trim();
    if (!q) return tableData;
    return tableData.filter((row) => safeLower(row?.__searchText).includes(q));
  }, [tableData, search]);

  const outerHeadings = useMemo(
    () =>
      headings.map((h) => {
        if (h.field !== "item_name" && h.field !== "unit_price" && h.field !== "tax")
          return h;

        return {
          ...h,
          renderCell: (row) => {
            const label = row?.[h.field] ?? "-";
            if (String(label) !== "Multiple items") return <span>{label}</span>;

            const itemsLen = Array.isArray(row?.__quotation?.items)
              ? row.__quotation.items.length
              : 0;

            const items = Array.isArray(row?.__quotation?.items) ? row.__quotation.items : [];
            const valueHeader = h.field === "tax" ? "Tax" : "Unit Price";
            const getValue = (it) => {
              if (h.field !== "tax") return formatRupees(it?.unit_price ?? "-");
              const txt = safeText(it?.tax, "-");
              if (txt === "-") return "-";
              return String(txt).includes("%") ? String(txt) : `${txt}%`;
            };

            const preview = items.slice(0, 6).map((it, idx) => ({
              key: it?.token || `${idx}`,
              name: safeText(it?.item_name),
              value: getValue(it),
            }));
            const remaining = Math.max(0, items.length - preview.length);

            return (
              <Tooltip
                arrow
                placement="bottom-start"
                disableInteractive
                enterDelay={250}
                leaveDelay={80}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "#ffffff",
                      color: "#0f172a",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 16px 45px rgba(15, 23, 42, 0.18)",
                      borderRadius: "12px",
                      p: 1.1,
                      maxWidth: 420,
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#ffffff",
                      "&::before": {
                        border: "1px solid #e2e8f0",
                        boxSizing: "border-box",
                      },
                    },
                  },
                }}
                title={
                  <Box sx={{ width: 380, maxWidth: "80vw" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 1,
                        alignItems: "center",
                        pb: 0.75,
                        mb: 0.75,
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "gilroyBold",
                          fontSize: 12.5,
                          color: "#334155",
                        }}
                      >
                        Item
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "gilroyBold",
                          fontSize: 12.5,
                          color: "#334155",
                        }}
                      >
                        {valueHeader}
                      </Typography>
                    </Box>
                    {preview.map((p) => (
                      <Box
                        key={p.key}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr auto",
                          gap: 1,
                          py: 0.55,
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "gilroyRegular",
                            fontSize: 12.5,
                            lineHeight: 1.25,
                            pr: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 250,
                          }}
                        >
                          {p.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "gilroyMedium",
                            fontSize: 12.5,
                            lineHeight: 1.25,
                            textAlign: "right",
                          }}
                        >
                          {p.value}
                        </Typography>
                      </Box>
                    ))}
                    {remaining ? (
                      <Typography
                        sx={{
                          fontFamily: "gilroyMedium",
                          fontSize: 12,
                          mt: 0.5,
                          color: "#475569",
                        }}
                      >
                        +{remaining} more
                      </Typography>
                    ) : null}
                  </Box>
                }
              >
                <span style={{ display: "inline-block" }}>
                  <button
                    type="button"
                    style={itemLinkStyle}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    Multiple items{itemsLen ? ` (${itemsLen})` : ""}
                  </button>
                </span>
              </Tooltip>
            );
          },
        };
      }),
    [headings, itemLinkStyle],
  );

  const deleteQuotation = async () => {
    const target = deleteTarget;
    if (!target) return;
    setDeleteLoading(true);

    try {
      const quotationToken = String(target?.quotation_token || "").trim();

      if (!quotationToken) {
        setDeleteQuotationOpen(false);
        setStatusModal({
          open: true,
          title: "Delete Failed",
          description: "No quotation token found for this row.",
          icon: deletePopupImg,
        });
        return;
      }

      await axios.post(
        API_ENDPOINT.delete_unregistered_vendor_quotation_doc,
        {
          vendor_token: vendorToken,
          quotation_token: quotationToken,
          // token: quotationToken,
        },
        authToken ? { headers: { Authorization: `Token ${authToken}` } } : undefined,
      );

      setDeleteQuotationOpen(false);
      setStatusModal({
        open: true,
        title: "Deleted Successfully!",
        description: "Deleted quotation successfully.",
        icon: successPopupImg,
      });

      // Optimistically update current page by removing deleted quotation
      setQuotations((prev) =>
        (Array.isArray(prev) ? prev : []).filter((q) => {
          const qToken = q?.quotation_token || q?.quotationToken || q?.id;
          return qToken !== quotationToken;
        }),
      );

      await fetchQuotations({ page: pageNumber, q: search });
    } catch (err) {
      setDeleteQuotationOpen(false);
      setStatusModal({
        open: true,
        title: "Delete Failed",
        description:
          err?.response?.data?.message ||
          err?.response?.data?.response ||
          "Unable to delete quotation.",
        icon: deletePopupImg,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const viewItemHeadings = useMemo(() => {
    const items = Array.isArray(activeQuotation?.items) ? activeQuotation.items : [];
    const warranties = uniqTexts(items.map((it) => it?.warranty?.warranty_raw_text));
    const showWarrantyColumn = warranties.length !== 1;

    const base = [
      { label: "Item Name", field: "item_name", col_width: "220px" },
      { label: "Model No", field: "model_number", col_width: "160px" },
      // { label: "Serial No", field: "serial_number", col_width: "160px" },
      {
        label: "Specification",
        field: "description",
        col_width: "360px",
        cellType: "readMore",
        maxLength: 90,
      },
      { label: "Basic Price", field: "unit_price", col_width: "140px" },
      // { label: "Tax (%)", field: "tax", col_width: "100px" },
      // { label: "Item Total", field: "total_price", col_width: "160px" },
      { label: "Warranty", field: "warranty_period", col_width: "180px" },
    ];

    return showWarrantyColumn
      ? [
          ...base,
          {
            label: "Warranty",
            field: "warranty_raw_text",
            col_width: "420px",
            cellType: "readMore",
            maxLength: 90,
          },
        ]
      : base;
  }, [activeQuotation]);

  const viewItemRows = useMemo(() => {
    const items = Array.isArray(activeQuotation?.items) ? activeQuotation.items : [];
    const rows = items.map((it, idx) => {
      const unit = toNumber(it?.unit_price);
      const taxPct = toNumber(it?.tax);
      const lineGst = taxPct ? (unit * taxPct) / 100 : 0;
      const lineTotal = unit + lineGst;

      return {
        token: it?.token || it?.part_number || `${idx + 1}`,
        item_name: safeText(it?.item_name),
        model_number: safeText(it?.model_number),
        serial_number: safeText(it?.serial_number),
        description: safeText(it?.description),
        unit_price: formatRupees(it?.unit_price ?? "-"),
        tax: it?.tax ?? "-",
        total_price: Number.isFinite(lineTotal) ? formatRupees(lineTotal) : "-",
        warranty_period: safeText(it?.warranty?.warranty_period),
        warranty_raw_text: safeText(it?.warranty?.warranty_raw_text),
      };
    });

    const q = safeLower(itemSearch).trim();
    if (!q) return rows;
    return rows.filter((row) =>
      [
        "item_name",
        "model_number",
        "serial_number",
        "description",
        "unit_price",
        "tax",
        "total_price",
        "warranty_period",
        "warranty_raw_text",
      ].some(
        (f) => safeLower(row[f]).includes(q),
      ),
    );
  }, [activeQuotation, itemSearch]);

  const commonWarrantyText = useMemo(() => {
    const items = Array.isArray(activeQuotation?.items) ? activeQuotation.items : [];
    const warranties = uniqTexts(items.map((it) => it?.warranty?.warranty_raw_text));
    if (warranties.length !== 1) return "";
    const w = warranties[0];
    return w && w !== "-" ? w : "";
  }, [activeQuotation]);

  const renderReadMoreBlock = useCallback(
    ({ title, text, maxLen = 180 }) => {
      const trimmed = safeText(text, "").trim();
      if (!trimmed) return null;
      const needsMore = trimmed.length > maxLen;
      const shortText = needsMore ? `${trimmed.slice(0, maxLen).trim()}...` : trimmed;

      return (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontFamily: "gilroyBold", fontSize: 14, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              fontSize: 13,
              fontFamily: "gilroyRegular",
              lineHeight: 1.35,
            }}
          >
            {shortText || "-"}{" "}
            {needsMore ? (
              <button
                type="button"
                style={itemLinkStyle}
                onClick={() => {
                  setCommonTextTitle(title);
                  setCommonTextValue(trimmed);
                  setCommonTextOpen(true);
                }}
              >
                Read more
              </button>
            ) : null}
          </Typography>
        </Box>
      );
    },
    [itemLinkStyle],
  );

  return (
    <Box className={styles.container}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <HeaderTitle titleText="Price List" />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SearchBar
            value={search}
            onChange={(q) => {
              setSearch(q);
              setPageNumber(1);
            }}
            placeholder="Search"
          />
          <DButton
            label={"Add Price List"}
            btnHeight={"4rem"}
            btnWidth={"14rem"}
            vendorBtn={true}
            onClick={() => navigate("/UnregisteredVendor_dashboard/catalog/add")}
          />
        </Box>
      </Box>

      <div>
        <DataTable
          headings={outerHeadings}
          tableData={filteredData}
          actions={true}
          viewBtn={true}
          deleteBtn={true}
          stickyFirstColumn={true}
          fixedLayout={true}
          viewClick={(_, row) => {
            const q = row?.__quotation || null;
            if (!q) return;
            setActiveQuotation(q);
            setItemSearch("");
            setViewOpen(true);
          }}
          deleteClick={(_, row) => {
            setDeleteTarget({
              quotation_token:
                row?.quotation_token ||
                row?.__quotation?.quotation_token ||
                row?.__quotation?.token ||
                "",
            });
            setDeleteQuotationOpen(true);
          }}
          allowDeleteForNonAdmin={true}
          sorting={true}
          tableSize={"100%"}
          isLoading={tableProps.isLoading}
          errorMessage={tableProps.errorMessage}
        />
      </div>

      <Modal open={viewOpen} onClose={() => setViewOpen(false)}>
        <Fade in={viewOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(94vw, 1200px)",
              maxHeight: "88vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.2)",
              p: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 1.5,
                position: "sticky",
                top: 0,
                zIndex: 6,
                background: "#fff",
                borderBottom: "1px solid #e2e8f0",
                pb: 1,
              }}
            >
              <Box>
                <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18 }}>
                  Quotation Details
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.5 }}>
                  {safeText(activeQuotation?.quotation_name || activeQuotation?.file_name, "-")}
                  {"  •  "}
                  {Array.isArray(activeQuotation?.items)
                    ? `${activeQuotation.items.length} item(s)`
                    : "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                {activeQuotation?.document ? (
                  <Button
                    variant="outlined"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontFamily: "gilroyBold",
                      px: 2.5,
                      py: 1,
                      fontSize: 14,
                    }}
                    onClick={() => {
                      const url = buildDocumentUrl(activeQuotation?.document);
                      if (url) window.open(url, "_blank", "noopener,noreferrer");
                    }}
                  >
                    View PDF
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  size="medium"
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontFamily: "gilroyBold",
                    px: 3,
                    py: 1,
                    minWidth: 110,
                    fontSize: 14,
                  }}
                  onClick={() => setViewOpen(false)}
                >
                  Close
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                flexWrap: "wrap",
                gap: 1.5,
                mb: 1.25,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexWrap: "wrap",
                  gap: 2,
                  flex: 1,
                  minWidth: 320,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                    Created:
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {safeText(activeQuotation?.created_date, "-")}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 280,
                    maxWidth: 520,
                    fontFamily: "gilroyRegular",
                    "& input": { fontFamily: "gilroyRegular" },
                  }}
                >
                  <SearchBar
                    value={itemSearch}
                    onChange={setItemSearch}
                    placeholder="Search items"
                    customClass={styles.modalSearchBar}
                  />
                </Box>
              </Box>

              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                  Grand Total:
                </Typography>
                <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18 }}>
                  {formatRupees(activeQuotation?.totals?.total ?? 0)}
                </Typography>
              </Box> */}
            </Box>

            <QuotationItemsTable
              headings={viewItemHeadings}
              rows={viewItemRows}
              onView={null}
              showActions={false}
              stickyFirstColumn={false}
            />

            {commonWarrantyText
              ? renderReadMoreBlock({ title: "Warranty", text: commonWarrantyText })
              : null}
          </Box>
        </Fade>
      </Modal>

      <Modal open={commonTextOpen} onClose={() => setCommonTextOpen(false)}>
        <Fade in={commonTextOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(92vw, 780px)",
              maxHeight: "86vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.2)",
              p: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18 }}>
                {commonTextTitle || "Details"}
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontFamily: "gilroyBold",
                  px: 3,
                  py: 1,
                  minWidth: 110,
                  fontSize: 14,
                }}
                onClick={() => setCommonTextOpen(false)}
              >
                Close
              </Button>
            </Box>
            <Typography
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: 14,
                fontFamily: "gilroyRegular",
                lineHeight: 1.45,
              }}
            >
              {commonTextValue || "-"}
            </Typography>
          </Box>
        </Fade>
      </Modal>

      <PaginantionComponent
        pageNumber={pageNumber}
        currentPage={paginationData?.page || 1}
        itemsPerPage={paginationData?.current_page_count || 0}
        totalItems={paginationData?.page_count || 0}
        localStorageKey={"unregistered_vendor_quotation_page_number"}
        previouPageClick={() => previouPageClick(pageNumber, setPageNumber)}
        nextPageClick={() =>
          nextPageClick(pageNumber, setPageNumber, paginationData)
        }
        setPageNumber={setPageNumber}
        disablePrevious={parseInt(pageNumber) === 1 || pageNumber === ""}
        disableNext={
          parseInt(pageNumber) === paginationData?.page_count ||
          !paginationData ||
          pageNumber === ""
        }
      />

      {/* Delete confirmation */}
      <Modal open={deleteQuotationOpen}>
        <Fade in={deleteQuotationOpen}>
          <Box>
            <SuccessPopupDashboard
              popupImage={deletePopupImg}
              popupHeroText="Are you sure want to delete?"
              popupDescription={""}
              popBtn={true}
              btnText={"Delete"}
              loadingPrimaryBtn={deleteLoading}
              secondaryBtn={true}
              vendorBtn={true}
              vendorBtnOutlined={true}
              btnTextSecondary={"Cancel"}
              btnClick={deleteQuotation}
              btnClickSecondary={() => setDeleteQuotationOpen(false)}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Status modal for update/delete */}
      <Modal open={statusModal.open}>
        <Fade in={statusModal.open}>
          <Box>
            <SuccessPopupDashboard
              popupImage={statusModal.icon}
              popupHeroText={statusModal.title}
              popupDescriptionLG={statusModal.description}
              popBtn={true}
              vendorBtn={true}
              btnText={"Okay"}
              btnClick={() =>
                setStatusModal((p) => ({ ...p, open: false }))
              }
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default UnregisteredVendorCatalogTab;
